/**
 * KAN-562 — "Finish this first, then your course opens" banner.
 *
 * The profile page's half of the course-player gate. It renders only when
 * profileCompletionGuard sent the learner here (`?returnTo=/courses/:id`), so
 * an ordinary visit to the profile page is completely unaffected.
 *
 * Two states, both in plain English — the reader is a learner who clicked a
 * course and got a settings page instead, and is owed an explanation:
 *   - still missing fields: why they are here and what to do
 *   - all filled in: a one-click way back into the course they wanted
 *
 * Completion is passed in by the profile page (fed from the My Information
 * section, which owns the field values), so this component stays presentational.
 */
import { ChangeDetectionStrategy, Component, ElementRef, effect, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { REQUESTED_FIELD_BADGE_LABEL } from './profile-fields.utils';

@Component({
  selector: 'app-profile-gate-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  templateUrl: './profile-gate-banner.component.html',
  styleUrls: ['./profile-gate-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileGateBannerComponent {
  /** The course the learner was trying to open, already sanitized, or null. */
  readonly returnUrl = input<string | null>(null);

  /** True once every field the organization asked for has a value. */
  readonly isComplete = input<boolean>(false);

  /**
   * The exact word printed on the badge next to each outstanding field, so
   * the instruction below names what the learner can actually see.
   */
  readonly fieldBadgeLabel = REQUESTED_FIELD_BADGE_LABEL;

  private readonly host = inject(ElementRef<HTMLElement>);

  /**
   * Bring the banner back into view the moment it flips to "ready".
   *
   * The learner saves the last requested field while scrolled down at the My
   * Information card, and this banner renders at the TOP of the profile page.
   * Measured live on 2026-09-14: window.scrollY 364, banner top -316 - the
   * confirmation and the only "Go to my course" button on the page both
   * appeared entirely outside the viewport, so from the learner's seat
   * nothing happened at all.
   */
  constructor() {
    effect(() => {
      if (!this.isComplete() || !this.returnUrl()) {
        return;
      }
      const el = this.host.nativeElement as HTMLElement | null;
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  }
}
